/**
 * HomePage — Chat + voice interface.
 *
 * Layout: scrollable message thread (flex-1) + sticky input bar.
 * Voice:  mic capture + the configured STT/TTS path (browser or
 *         ElevenLabs). Native-audio live voice was removed ahead of a
 *         LiveKit/Pipecat rebuild.
 * Orb:    click to go live, click again to stop. Server-side VAD
 *         endpoints each utterance — no hold-to-talk.
 */
import * as React from "react";
import { useLiveStream } from "@/api/ws/useLiveStream";
import { cn } from "@/lib/cn";
import {
  Send,
  SquarePen,
  ChevronUp,
  Paperclip,
  X,
} from "lucide-react";
import { useApiClient } from "@/api/useApiClient";
import { uploadArtifact as uploadFile } from "@/lib/upload";
import { Waveform } from "@/components/voice/Waveform";
import { MicPolarOrb } from "@/components/voice/MicPolarOrb";
import { ThinkingPulse } from "@/components/voice/ThinkingPulse";
import {
  ToolCallGroup,
  type ToolCallView,
} from "@/components/chat/ToolCallBubble";
import {
  CHAT_INDENT,
  ChatAvatar,
  ChatError,
  ChatMessage,
} from "@/components/chat/ChatMessage";
import {
  ChatSlashDropdown,
  filterSlashItems,
  slashQuery,
} from "@/components/chat/ChatSlashDropdown";
import { getToolsManifest } from "@/api";
import type { ToolManifestItem } from "@/api";
import { useVoiceCall } from "@/components/voice/VoiceCallContext";
import { useSessionMessages } from "@/hooks/useSessionMessages";
import { useSessionPanel } from "@/contexts/SessionPanelContext";
import { ContextStatusRing } from "@/components/shared/ContextStatusRing";

// ─── Types ───────────────────────────────────────────────────────────────────

type AgentState = "idle" | "listening" | "thinking" | "speaking";

interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Correlation id of the WS turn that produced this row. Carried so
   *  tool rows reloaded from the server can be associated with the
   *  user message that triggered them. Local-only messages added via
   *  ``addMessage`` may leave this undefined. */
  correlation_id?: string | null;
}

interface PendingAttachment {
  key: string;            // ULID-based attachment key for de-dup
  filename: string;
  size: number;
  contentType: string;
  artifactId?: string;    // populated once upload completes
  status: "uploading" | "ready" | "error";
  error?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let _ctr = 0;
const nextId = () => `m${++_ctr}_${Date.now()}`;

// ─── VoiceOrb (thin Dyson Sphere wrapper) ────────────────────────────────────
//
// Delegates the visual to the shared :class:`DysonSphere` component so any
// tweak to the brand mark in DysonSphere.tsx propagates everywhere (sidebar
// logo, splash, voice orb). The orb adds nothing beyond the button gestures.

import { DysonSphere, type DysonMode } from "@/components/ui/DysonSphere";
import { apiErrorMessage } from "@/lib/api-error";
import { useLocation, useNavigate } from "react-router-dom";
import { COMPOSE_EVENT, type ComposeDetail } from "@/modules/pane/askInChat";

const AGENT_MODE_MAP: Record<AgentState, DysonMode> = {
  idle: "idle",
  listening: "listening",
  thinking: "thinking",
  speaking: "speaking",
};

function VoiceOrb({
  state,
  micVolume,
  size = 56,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  pressed,
  testId,
  className,
}: {
  state: AgentState;
  micVolume: number;
  size?: number;
  onClick?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onMouseLeave?: () => void;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  pressed?: boolean;
  testId?: string;
  className?: string;
}) {
  const Tag = (onClick || onMouseDown ? "button" : "div") as React.ElementType;
  const active = state === "listening" || state === "speaking";

  return (
    <Tag
      {...(onClick || onMouseDown
        ? ({
            type: "button",
            ...(onClick ? { onClick } : {}),
            ...(onMouseDown ? { onMouseDown } : {}),
            ...(onMouseUp ? { onMouseUp } : {}),
            ...(onMouseLeave ? { onMouseLeave } : {}),
            ...(onTouchStart ? { onTouchStart } : {}),
            ...(onTouchEnd ? { onTouchEnd } : {}),
            "aria-label": `Voice control — ${state}`,
            "aria-pressed": pressed ?? active,
            ...(testId ? { "data-testid": testId } : {}),
          } as React.ButtonHTMLAttributes<HTMLButtonElement>)
        : {})}
      className={cn(
        "relative select-none",
        onClick &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--om-color-focus-ring)]",
        className,
      )}
      data-state={state}
      style={{ width: size, height: size }}
    >
      <DysonSphere
        mode={AGENT_MODE_MAP[state]}
        glow={active ? micVolume : 0}
        className="w-full h-full"
        // Pass size through so DysonSphere's ``variant="auto"`` can
        // downshift to the flat-SVG mini renderer at <32 px. Without
        // this, EVERY VoiceOrb (the inline 24 px streaming indicator,
        // the 40 px composer toggle, the 56 px hero) renders the full
        // CSS-3D scene with 10+ concurrent animations — three of those
        // on one page tanks the renderer. The mini variant is a
        // 3-ellipse SVG with one pulse keyframe, ~100× cheaper.
        size={size}
      />
    </Tag>
  );
}

// ─── HomePage ────────────────────────────────────────────────────────────────

export function HomePage() {
  const {
    voiceActive,
    agentState,
    micVolume,
    interimText,
    available: voiceAvailable,
    turnsCompleted,
    toggleVoice,
    cancelActiveCall,
  } = useVoiceCall();

  // ── Server-backed session + message history ───────────────────────────────
  // sessionId is the ULID persisted in omni_sessions on the server.
  // localStorage stores ONLY this opaque id — never message content.
  const {
    sessionId,
    messages: rawMessages,
    addMessage,
    loading: sessionLoading,
    loadingMore,
    hasMore,
    loadMore,
    newSession,
    switchSession,
    refreshMessages,
  } = useSessionMessages();
  // Scoped to this chat: replies for the user's other chats are not ours.
  const live = useLiveStream(sessionId);

  const { setActiveSessionId, setHandlers, refetch: refetchSessionList } = useSessionPanel();

  React.useEffect(() => {
    if (!sessionId) return;
    const tick = (): void => {
      if (document.visibilityState === "visible") {
        void refreshMessages();
        void refetchSessionList();
      }
    };
    const id = window.setInterval(tick, 15_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [sessionId, refreshMessages, refetchSessionList]);

  // A spoken turn is written into this session by the server (the voice
  // worker sends it through the gateway like a typed one); show it as soon
  // as Omni stops speaking instead of waiting for the 15 s poll.
  React.useEffect(() => {
    if (turnsCompleted > 0) void refreshMessages();
  }, [turnsCompleted, refreshMessages]);

  // Refresh session rail when messages change (new turn persisted).
  React.useEffect(() => {
    if (rawMessages.length === 0) return;
    void refetchSessionList();
  }, [rawMessages.length, sessionId, refetchSessionList]);

  // The server returns user, assistant AND tool rows. Tool rows render
  // as accordions, not message bubbles, so split them here. Messages
  // keeps the conversational thread; persistedToolRows feeds the tool
  // hydration effect below.
  const messages = React.useMemo<Message[]>(
    () =>
      rawMessages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          text: m.text,
          correlation_id: m.correlation_id ?? null,
        })),
    [rawMessages],
  );
  const persistedToolRows = React.useMemo(
    () => rawMessages.filter((m) => m.role === "tool"),
    [rawMessages],
  );

  const [inputText, setInputText] = React.useState("");

  // WS replay guard — behavioral, NOT text-based.
  // Any assistant.final that arrives before the user has sent anything in
  // this mount cycle is a stale backend replay; drop it regardless of content.
  const hasSentThisSessionRef = React.useRef(false);
  const lastFinalRef = React.useRef<string | null>(null);
  const lastSendRef = React.useRef<{
    key: string;
    expiresAt: number;
  } | null>(null);

  // refs
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const apiClient = useApiClient();

  // Text another surface asks to put in the composer — a pane tile's
  // Refresh (modules/pane/askInChat.ts). Filled, focused, not sent.
  const location = useLocation();
  const navigate = useNavigate();
  const fillComposer = React.useCallback((text: string) => {
    setInputText(text);
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);
  React.useEffect(() => {
    const onCompose = (e: Event) => {
      const detail = (e as CustomEvent<ComposeDetail>).detail;
      if (!detail?.text) return;
      e.preventDefault(); // tells the sender a composer took it
      fillComposer(detail.text);
    };
    window.addEventListener(COMPOSE_EVENT, onCompose);
    return () => window.removeEventListener(COMPOSE_EVENT, onCompose);
  }, [fillComposer]);
  React.useEffect(() => {
    const compose = (location.state as { compose?: unknown } | null)?.compose;
    if (typeof compose !== "string" || !compose) return;
    fillComposer(compose);
    // Once: a reload or back must not put it there again.
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location, navigate, fillComposer]);
  const [attachments, setAttachments] = React.useState<PendingAttachment[]>([]);

  // ── "/" tool autocomplete (PROD-2b) ────────────────────────────────────
  // Match + filter logic lives in ChatSlashDropdown.tsx so it's unit-
  // testable without mounting this whole (WS + voice + session) page.
  const slashQ = slashQuery(inputText);
  const [toolManifest, setToolManifest] = React.useState<ToolManifestItem[] | null>(null);
  const [slashActiveIdx, setSlashActiveIdx] = React.useState(0);

  React.useEffect(() => {
    if (slashQ === null || toolManifest !== null) return;
    let cancelled = false;
    getToolsManifest({ client: apiClient, query: { role: "frontier" } })
      .then(({ data }) => {
        if (!cancelled && data) setToolManifest(data.tools);
      })
      .catch(() => {
        // Best-effort — the textarea works fine without autocomplete.
      });
    return () => {
      cancelled = true;
    };
  }, [slashQ, toolManifest, apiClient]);

  const slashItems = React.useMemo(
    () =>
      slashQ === null || !toolManifest ? [] : filterSlashItems(toolManifest, slashQ),
    [slashQ, toolManifest],
  );

  React.useEffect(() => {
    setSlashActiveIdx(0);
  }, [slashItems]);

  const selectSlashItem = React.useCallback((tool: ToolManifestItem) => {
    setInputText(`${tool.title} `);
    setSlashActiveIdx(0);
    textareaRef.current?.focus();
  }, []);

  const handleFilesPicked = React.useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const additions: PendingAttachment[] = Array.from(files).map((f) => ({
        key: `${Date.now()}-${f.name}-${f.size}`,
        filename: f.name,
        size: f.size,
        contentType: f.type || "application/octet-stream",
        status: "uploading",
      }));
      setAttachments((prev) => [...prev, ...additions]);

      // Kick off uploads in parallel — failures are surfaced inline
      // on the chip so the user can drop + retry.
      additions.forEach((att, idx) => {
        const file = files[idx];
        uploadFile(apiClient, file)
          .then(({ artifactId }) => {
            setAttachments((prev) =>
              prev.map((p) =>
                p.key === att.key
                  ? { ...p, status: "ready", artifactId }
                  : p,
              ),
            );
          })
          .catch((e: unknown) => {
            setAttachments((prev) =>
              prev.map((p) =>
                p.key === att.key
                  ? {
                      ...p,
                      status: "error",
                      error: apiErrorMessage(e),
                    }
                  : p,
              ),
            );
          });
      });
    },
    [apiClient],
  );

  const removeAttachment = React.useCallback((key: string) => {
    setAttachments((prev) => prev.filter((p) => p.key !== key));
  }, []);
  // refs

  // Auto-scroll
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, live.streaming]);

  // Last user-message id added in this mount cycle. Tool events arriving
  // BEFORE any user message in this mount are orphans (rendered at the
  // end of the thread); events arriving after a user message latch onto
  // it so the tool accordion slots in right after the bubble that
  // triggered it.
  const lastUserMessageIdRef = React.useRef<string | null>(null);

  // Append the user's spoken turn as a chat-history bubble when the
  // backend emits ``user.transcript {final: true}``. Mirrors the typed
  // ``user.utterance`` flow where the user's message immediately renders.
  // Without this, voice turns produced an assistant reply with NO matching
  // user bubble above it — the spoken question only flashed in the
  // ephemeral "you: …" caption and was never preserved in the thread.
  // Set ``hasSentThisSessionRef`` here too because the user just spoke —
  // the next assistant.final is genuinely theirs, not a stale replay.
  const lastUserVoiceRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const t = live.lastUserUtterance;
    if (!t) return;
    if (t === lastUserVoiceRef.current) return; // duplicate guard
    lastUserVoiceRef.current = t;
    hasSentThisSessionRef.current = true;
    const id = nextId();
    lastUserMessageIdRef.current = id;
    addMessage({ id, role: "user", text: t });
  }, [live.lastUserUtterance, addMessage]);

  // Handle final assistant response.
  // hasSentThisSessionRef gates out stale backend replays on remount.
  React.useEffect(() => {
    if (!live.finalText) return;
    if (!hasSentThisSessionRef.current) return; // stale replay — drop
    if (live.finalText === lastFinalRef.current) return; // duplicate guard
    lastFinalRef.current = live.finalText;
    addMessage({ id: nextId(), role: "assistant", text: live.finalText });
  }, [live.finalText, addMessage]);

  // Tool calls, by tool_use_id. Live calls come merged from the socket
  // (useLiveStream); each records the user message it followed
  // (``messageId``) the first time it is seen, so the render pass can slot
  // it in right after that bubble. Calls from other sessions (another tab,
  // a background agent) are not this chat's.
  const [toolCalls, setToolCalls] = React.useState<ToolCallView[]>([]);

  // Reset whenever the active session flips. Declared before the merges
  // below so a switch clears first and they refill (effects run in order).
  React.useEffect(() => {
    setToolCalls([]);
  }, [sessionId]);

  React.useEffect(() => {
    // The hook drops other chats' events as they arrive, but its map still
    // holds the previous chat's calls after a switch.
    const mine = Object.values(live.toolCalls).filter((t) => t.session_id === sessionId);
    if (mine.length === 0) return;
    setToolCalls((prev) => {
      const next = prev.slice();
      const at = new Map(next.map((t, i) => [t.tool_use_id, i] as const));
      for (const { session_id: _session, ...call } of mine) {
        const i = at.get(call.tool_use_id);
        if (i === undefined) {
          at.set(call.tool_use_id, next.length);
          next.push({ ...call, messageId: lastUserMessageIdRef.current });
        } else {
          next[i] = { ...next[i], ...call, messageId: next[i].messageId };
        }
      }
      return next;
    });
  }, [live.toolCalls, sessionId]);

  // Hydrate toolCalls from the server's persisted tool rows. Tool
  // rows carry the WS correlation_id of the turn that spawned them,
  // and the user message that triggered them carries the same id —
  // so we associate by correlation_id. Server entries always take
  // precedence over live ones (the server has the canonical status
  // and result); duplicates by tool_use_id are merged.
  React.useEffect(() => {
    if (persistedToolRows.length === 0) return;
    const userMsgByCorrelation = new Map<string, string>();
    for (const m of messages) {
      if (m.role === "user" && m.correlation_id) {
        userMsgByCorrelation.set(m.correlation_id, m.id);
      }
    }
    setToolCalls((prev) => {
      const next = [...prev];
      const idxByToolUseId = new Map(
        next.map((t, i) => [t.tool_use_id, i] as const),
      );
      for (const row of persistedToolRows) {
        const meta = (row.meta ?? {}) as {
          tool_use_id?: string;
          name?: string;
          arguments?: Record<string, unknown>;
          status?: string;
          result?: unknown;
          icon?: string;
        };
        const toolUseId = String(meta.tool_use_id ?? "");
        if (!toolUseId) continue;
        const messageId =
          (row.correlation_id && userMsgByCorrelation.get(row.correlation_id)) ||
          null;
        const view: ToolCallView = {
          tool_use_id: toolUseId,
          name: String(meta.name ?? row.text ?? ""),
          arguments: meta.arguments ?? {},
          status:
            meta.status === "pending" || meta.status === "ok" || meta.status === "error"
              ? meta.status
              : "ok",
          result: meta.result,
          icon: meta.icon ?? null,
          messageId,
        };
        const existingIdx = idxByToolUseId.get(toolUseId);
        if (existingIdx === undefined) {
          idxByToolUseId.set(toolUseId, next.length);
          next.push(view);
        } else {
          // Keep the live link to the user bubble if the row cannot be
          // matched by correlation (a turn typed in this mount).
          next[existingIdx] = {
            ...next[existingIdx],
            ...view,
            messageId: view.messageId ?? next[existingIdx].messageId,
          };
        }
      }
      return next;
    });
  }, [persistedToolRows, messages]);
  // ``clearSession`` wipes these; defined below to avoid forward ref.

  // ── New chat ──────────────────────────────────────────────────────────────

  const clearSession = React.useCallback(() => {
    cancelActiveCall();
    lastFinalRef.current = null;
    hasSentThisSessionRef.current = false;
    lastUserMessageIdRef.current = null;
    setToolCalls([]);
    void newSession();
  }, [cancelActiveCall, newSession]);

  const handleSelectSession = React.useCallback(
    (id: string) => {
      cancelActiveCall();
      lastFinalRef.current = null;
      hasSentThisSessionRef.current = false;
      lastUserMessageIdRef.current = null;
      setToolCalls([]);
      void switchSession(id);
    },
    [cancelActiveCall, switchSession],
  );

  React.useEffect(() => {
    setActiveSessionId(sessionId);
  }, [sessionId, setActiveSessionId]);

  React.useEffect(() => {
    setHandlers({
      onSelect: handleSelectSession,
      onNewChat: clearSession,
    });
  }, [setHandlers, handleSelectSession, clearSession]);

  // ── Send text message ──────────────────────────────────────────────────────

  const sendMessage = React.useCallback(
    (text: string) => {
      const t = text.trim();
      if (!t || !sessionId) return;

      // Compose: when there are attachments, prepend a single-line
      // reference so the agent knows what files were uploaded
      // alongside this turn. The artifact ids resolve via the
      // storage service if a tool needs to fetch them. Only "ready"
      // attachments are included; uploading/errored ones are
      // silently dropped (the chip stays so the user sees the
      // state).
      const ready = attachments.filter((a) => a.status === "ready");
      const composed = ready.length
        ? `[Attached files: ${ready
            .map((a) => `${a.filename} (artifact:${a.artifactId})`)
            .join(", ")}]\n${t}`
        : t;
      const dedupBasis = `${sessionId}\n${composed}`;
      const now = Date.now();
      if (
        lastSendRef.current?.key === dedupBasis &&
        lastSendRef.current.expiresAt > now
      ) {
        return;
      }
      lastSendRef.current = { key: dedupBasis, expiresAt: now + 1500 };

      hasSentThisSessionRef.current = true;
      const id = nextId();
      lastUserMessageIdRef.current = id;
      addMessage({ id, role: "user", text: composed });
      setInputText("");
      // Clear only the attachments that were sent — keep any still
      // uploading so the user can send a follow-up that includes them.
      setAttachments((prev) => prev.filter((a) => a.status !== "ready"));
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      lastFinalRef.current = null;
      live.say(composed, sessionId, id);
    },
    [live.say, sessionId, addMessage, attachments],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The "/" dropdown owns the arrow keys, Enter, and Escape while
      // it's open — Enter picks a command instead of sending a bare
      // "/foo" to the agent (PROD-2b).
      if (slashItems.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashActiveIdx((i) => Math.min(i + 1, slashItems.length - 1));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashActiveIdx((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setInputText("");
          return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const picked = slashItems[slashActiveIdx];
          if (picked) selectSlashItem(picked);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage(inputText);
      }
    },
    [inputText, sendMessage, slashItems, slashActiveIdx, selectSlashItem],
  );

  // ─── Global Call Bridge ──────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────

  const hasMessages = messages.length > 0;
  const orphanToolCalls = toolCalls.filter((call) => !call.messageId);
  // Distinguish "input gate" from "agent activity":
  //   * isProcessing  → only true while the agent is `thinking`
  //     (the previous turn is genuinely in flight; we don't want
  //     the user spamming new turns on top of it).
  //   * isAgentActive → true during `thinking` OR `speaking`,
  //     used for visual indicators only.
  //
  // While the agent is `speaking` the text input STAYS ENABLED — the
  // user must be able to interrupt with a typed message at any time. Disabling the input here was the cause
  // of the "I can't type while the agent talks" bug (Docs/handoff
  // notes — spontaneous greeting puts agent in `speaking` state, which
  // used to lock the textarea).
  const isProcessing = agentState === "thinking";
  const isAnyFileUploading = attachments.some((a) => a.status === "uploading");


  return (
    // Peg the page to the viewport (minus the 44 px Topbar) so the inner
    // ``flex-1 overflow-y-auto`` messages list has a real upper bound. The
    // ambient AppShell main element (``flex-1 overflow-y-auto``) wraps
    // ``<Outlet />`` in a height-less ``mx-auto max-w-[1440px]`` div, so
    // ``h-full`` here used to resolve to the natural content height — the
    // whole HomePage grew past the viewport as chat messages accumulated,
    // the outer main element scrolled instead of the inner list, and the input
    // bar got pushed offscreen leaving the empty band the user reported.
    // ``100dvh`` adapts correctly to mobile dynamic-toolbar resizes.
    <div className="flex h-full min-h-0 flex-1 flex-col">

      {/* ── Messages area ─────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {sessionLoading ? (
          /* Loading skeleton while session/messages fetch is in flight */
          <div className="mx-auto max-w-3xl space-y-4 px-6 py-6">
            {[80, 60, 72, 50, 90].map((w, i) => (
              <div
                key={i}
                className={cn(
                  "flex gap-3",
                  i % 2 === 0 ? "flex-row" : "flex-row-reverse",
                )}
              >
                <div className="h-6 w-6 flex-shrink-0 bg-bg-elevated border border-border animate-pulse" />
                <div
                  className="h-10 bg-bg-elevated border border-border animate-pulse"
                  style={{ width: `${w}%` }}
                />
              </div>
            ))}
          </div>
        ) : !hasMessages ? (
          /* Empty state */
          <div className="flex h-full flex-col items-center justify-center gap-6 px-6 py-16 text-center">
            <VoiceOrb
              state={agentState}
              micVolume={micVolume}
              size={108}
              onClick={voiceAvailable ? toggleVoice : undefined}
            />
            <div>
              <h2 className="text-base font-semibold text-fg">
                Omni — One brain. Every device. Always on.
              </h2>
              <p className="mt-1.5 text-sm text-fg-muted">
                {voiceAvailable
                  ? "Tap the orb to speak, or type below"
                  : "Type a message to start"}
              </p>
            </div>
          </div>
        ) : (
          /* Message thread */
          <div className="mx-auto max-w-3xl space-y-5 px-6 py-6">

            {/* Load more (older messages) */}
            {hasMore && (
              <div className="flex justify-center pb-2">
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="flex items-center gap-1.5 text-xs font-mono text-fg-faint hover:text-fg-muted disabled:opacity-50 transition-colors"
                >
                  <ChevronUp size={12} />
                  {loadingMore ? "Loading…" : "Load earlier messages"}
                </button>
              </div>
            )}

            {messages.map((msg) => {
              // Tool calls render directly after the user message that
              // triggered them — user said X → tool Y ran → AI replied
              // Z. Only attach tools to user messages; AI messages don't
              // need a trailing tool list (the tools fired BEFORE the AI
              // spoke, so they're already above its bubble).
              const associatedTools =
                msg.role === "user"
                  ? toolCalls.filter((tool) => tool.messageId === msg.id)
                  : [];
              return (
                <React.Fragment key={msg.id}>
                  <ChatMessage
                    role={msg.role}
                    text={msg.text}
                    data-testid={msg.role === "assistant" ? "final" : undefined}
                  />
                  {associatedTools.length > 0 && (
                    <ToolCallGroup
                      calls={associatedTools}
                      className={CHAT_INDENT}
                      data-testid={`tool-calls-for-${msg.id}`}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {/* Orphan tool calls — tools whose triggering user message
                isn't in the current thread. Happens on reload (persisted
                ids reference the prior mount). Render at the end so the
                user still sees the tool history; precise positioning is
                only possible while the session stays mounted. */}
            {orphanToolCalls.length > 0 && (
              <ToolCallGroup
                calls={orphanToolCalls}
                data-testid="tool-calls-list"
              />
            )}

            {/* Streaming response */}
            {live.pending && live.streaming && (
              <ChatMessage
                role="assistant"
                text={live.streaming}
                streaming
                data-testid="transcript"
              />
            )}

            {/* Thinking indicator — animated sweep + cycling verb so
                the user gets a sense of progress while the agent works. */}
            {live.pending && !live.streaming && (
              <div className="flex items-center gap-3">
                <ChatAvatar role="assistant" mode="thinking" />
                <ThinkingPulse />
              </div>
            )}

            {/* Error */}
            {live.error && <ChatError message={live.error} />}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* ── Interim transcript ─────────────────────────────────────────────── */}
      {interimText && (
        <div className="border-t border-border bg-bg-sunken px-6 py-2 text-sm">
          <span className="text-fg-faint">you: </span>
          <span className="text-fg italic">{interimText}</span>
          <span className="ml-1 inline-block h-3.5 w-px animate-pulse bg-fg-muted align-middle" />
        </div>
      )}

      {/* ── Input area ────────────────────────────────────────────────────── */}
      <div className="border-t border-border bg-bg-elevated">

        <div className="mx-auto max-w-3xl px-6 py-4 space-y-3">

          {/* Attachment chips — shown above the textarea while files
              are uploading or queued for the next send. */}
          {attachments.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {attachments.map((a) => (
                  <div
                    key={a.key}
                    className={cn(
                      "flex max-w-full flex-col gap-0.5 border px-2 py-1.5 text-xs",
                      a.status === "error"
                        ? "border-danger bg-danger/5"
                        : a.status === "ready"
                        ? "border-border text-fg"
                        : "border-border text-fg-muted",
                    )}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {a.status === "uploading" && (
                        <span
                          className="w-3 h-3 flex-shrink-0 border-2 border-accent border-t-transparent rounded-full animate-spin"
                          aria-hidden
                        />
                      )}
                      {a.status === "ready" && (
                        <span className="text-success flex-shrink-0" aria-hidden>●</span>
                      )}
                      {a.status === "error" && (
                        <span className="text-danger flex-shrink-0" aria-hidden>✕</span>
                      )}
                      <span className="truncate max-w-[200px]">{a.filename}</span>
                      {a.status === "uploading" && (
                        <span className="text-[10px] font-mono text-fg-faint flex-shrink-0">
                          Uploading…
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.key)}
                        className="ml-auto opacity-60 hover:opacity-100 flex-shrink-0"
                        aria-label={`Remove ${a.filename}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                    {a.status === "error" && a.error && (
                      <p className="text-[10px] text-danger leading-snug" role="alert">
                        {a.error}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hidden file picker — accepts every file type (images,
              audio, video, PDFs, etc.) so the model can take in any
              attachment alongside the prompt. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="*/*"
            onChange={(e) => {
              handleFilesPicked(e.target.files);
              // Reset so picking the same file twice still fires onChange.
              if (e.target.value) e.target.value = "";
            }}
            className="sr-only"
            // Named like the button that opens it; unnamed, axe failed it.
            aria-label="Attach files"
            tabIndex={-1}
            data-testid="prompt-file-input"
          />

          {/* "/" tool autocomplete (PROD-2b) — sits directly above the
              composer. Selecting inserts the tool's title as a phrase
              for the agent to act on; it does NOT dispatch the tool
              (that's ⌘K's CommandPalette). */}
          <ChatSlashDropdown
            items={slashItems}
            activeIdx={slashActiveIdx}
            onHover={setSlashActiveIdx}
            onSelect={selectSlashItem}
          />

          {/* Text input. */}
          <div className="flex items-end gap-3 border border-border bg-bg px-4 py-3 focus-within:border-border-strong transition-colors">
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }}
              placeholder="Ask Omni anything…"
              rows={1}
              disabled={isProcessing}
              className="flex-1 resize-none overflow-hidden bg-transparent text-sm text-fg placeholder:text-fg-faint focus:outline-none min-h-[1.25rem] max-h-44 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => sendMessage(inputText)}
              // Not before the chat session is ready: sendMessage needs it and
              // returned silently, so a fast click on a slow load did nothing.
              disabled={!inputText.trim() || isProcessing || !sessionId}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center bg-accent text-accent-fg transition-opacity disabled:opacity-40 hover:opacity-90"
              aria-label="Send"
            >
              <Send size={13} />
            </button>
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-2">

            {/* Voice orb intentionally NOT mounted here. The
                composer used to host a 40 px Dyson orb but the
                page already shows the floating global orb (always
                visible) and the hero orb (empty state), and every
                full Dyson 3D scene runs ~10 concurrent CSS
                animations. Three of them on a single page tanks
                the renderer. Use the floating orb or the hero
                orb to toggle voice. */}

            {/* Playwright instrumentation — hidden counters / ready flags */}
            {sessionId && (
              <>
                <span
                  data-testid="session-ready"
                  aria-hidden="true"
                  style={{ display: "none" }}
                />
                {/* Hidden input to let E2E tests simulate voice transcripts */}
                <input
                  type="text"
                  aria-label="transcript draft"
                  style={{ display: "none" }}
                  onChange={(ev) => {
                    const val = ev.target.value;
                    if (val) {
                      live.say(val, sessionId);
                      ev.target.value = "";
                    }
                  }}
                />
              </>
            )}
            {/* Mic activity visualizer — organic polar-polygon blob that
                morphs to the user's voice, replaces the flat ``Waveform``
                bar. ``Waveform`` stays imported because the e2e tests
                target ``[data-testid=waveform-bar]``; the bar renders
                off-screen below so tests keep working. */}
            {voiceActive && (
              <>
                <MicPolarOrb rms={micVolume} size={36} />
                <span className="sr-only">
                  <Waveform rms={micVolume} />
                </span>
              </>
            )}

            {/* Attach files — any type incl. images, audio, video, PDFs */}
            <button
              type="button"
              title={isAnyFileUploading ? "Uploading files..." : "Attach files (any type — images, audio, video, documents)"}
              onClick={() => !isAnyFileUploading && fileInputRef.current?.click()}
              disabled={isAnyFileUploading}
              className={cn(
                "flex h-8 w-8 items-center justify-center transition-colors",
                isAnyFileUploading ? "text-accent cursor-not-allowed" : "text-fg-faint hover:text-fg-muted"
              )}
              aria-label="Attach files"
              data-testid="prompt-attach-button"
            >
              {isAnyFileUploading ? (
                <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              ) : (
                <Paperclip size={15} />
              )}
            </button>

            <div className="flex-1" />

            {/* Context-usage status — auto-compaction handled by ADK server-side */}
            {messages.length > 0 && <ContextStatusRing sessionId={sessionId} />}

            {/* New chat */}
            {messages.length > 0 && (
              <button
                type="button"
                title="New chat"
                onClick={clearSession}
                className="flex h-8 w-8 items-center justify-center text-fg-faint hover:text-fg-muted transition-colors"
                aria-label="New chat"
              >
                <SquarePen size={15} />
              </button>
            )}

            {/* Status dot */}
            <span
              className={cn(
                "text-[10px] font-mono",
                live.error ? "text-danger" : "text-fg-faint",
              )}
            >
              {live.error ? "⚠ disconnected" : "● connected"}
            </span>

          </div>
        </div>
      </div>
    </div>
  );
}
